import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Download, Mail, MapPin, Package, Truck, Sparkles } from "lucide-react";
import { formatPrice } from "@/lib/cart/pricing";
import { getOrder, type Order } from "@/lib/checkout/order";
import { SafeImage } from "@/components/common/SafeImage";
import { fallbackProductImage } from "@/lib/catalog/fallback-images";
import { normaliseOrderLines, totalUnits } from "@/lib/orders/pack-lines";
import { PackBreakdown } from "@/components/orders/PackBreakdown";

export const Route = createFileRoute("/order/$id")({
  head: ({ params }) => ({
    meta: [
      { title: `Order ${params.id} — CB16 Maison` },
      { name: "description", content: "Your CB16 Maison order confirmation." },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: `Order ${params.id} — CB16 Maison` },
      { property: "og:type", content: "website" },
    ],
  }),
  component: OrderPage,
});

function OrderPage() {
  const { id } = Route.useParams();
  const [order, setOrder] = useState<Order | null | undefined>(undefined);

  useEffect(() => {
    setOrder(getOrder(id));
  }, [id]);

  if (order === undefined) {
    return <SkeletonState />;
  }
  if (order === null) {
    return <NotFoundState id={id} />;
  }

  const placedAt = new Date(order.createdAt);
  const lines = normaliseOrderLines(order.lines);
  const units = totalUnits(lines);
  const eta = order.estimatedDeliveryAt ? new Date(order.estimatedDeliveryAt) : null;
  const paymentLabel =
    order.paymentMethod === "razorpay"
      ? "Razorpay"
      : order.paymentMethod === "card"
        ? `Card ending ${order.cardLast4 ?? "••••"}`
        : order.paymentMethod === "paypal"
          ? "PayPal"
          : "Apple Pay";

  return (
    <div className="bg-white">
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-10 lg:py-20">
        <div className="text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-[#C6A15B]" strokeWidth={1.5} />
          <p className="mt-6 text-[11px] uppercase tracking-[0.32em] text-[#C6A15B]">
            Payment Successful — Order Confirmed
          </p>
          <h1
            className="mt-3 text-3xl text-black sm:text-4xl"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Thank you for your order
          </h1>
          <p className="mt-3 text-sm text-black/60">
            Order <span className="font-medium text-black">{order.id}</span> · placed{" "}
            {placedAt.toLocaleDateString(undefined, {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </p>
          <p className="mt-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-black/50">
            <Mail className="h-3.5 w-3.5 text-[#C6A15B]" /> A confirmation has been sent to{" "}
            {order.email}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 border border-[#C6A15B]/40 bg-[#C6A15B]/5 px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-[#C6A15B]">
            Payment {order.paymentStatus ?? "paid"} · {paymentLabel}
          </div>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-3">
          <Card icon={<Package className="h-4 w-4 text-[#C6A15B]" />} title="Order Items">
            <p className="text-sm text-black/70">
              {lines.length} line{lines.length === 1 ? "" : "s"} · {units} bottle
              {units === 1 ? "" : "s"}
            </p>
          </Card>
          <Card icon={<Truck className="h-4 w-4 text-[#C6A15B]" />} title="Delivery">
            <p className="text-sm text-black/70">{order.delivery.label}</p>
            <p className="text-[11px] uppercase tracking-[0.22em] text-black/50">
              {eta
                ? `Est. ${eta.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`
                : order.delivery.eta}
            </p>
          </Card>
          <Card icon={<MapPin className="h-4 w-4 text-[#C6A15B]" />} title="Shipping To">
            <p className="text-sm text-black/70">{order.shipping.fullName}</p>
            <p className="text-[11px] text-black/50">
              {order.shipping.city}, {order.shipping.country}
            </p>
          </Card>
        </div>

        <div className="mt-10 border border-black/10 bg-white p-6">
          <h2
            className="mb-4 text-lg text-black"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Order Summary
          </h2>
          <ul className="divide-y divide-black/10">
            {lines.map((l) => (
              <li key={l.key} className="flex gap-4 py-4">
                <SafeImage
                  src={l.img}
                  fallback={fallbackProductImage(l.slug || l.key)}
                  alt=""
                  className="h-16 w-16 object-cover"
                />
                <div className="flex flex-1 flex-col justify-between">
                  <div>
                    <p className="text-sm text-black">{l.name}</p>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-black/50">
                      {l.variantLabel} · Qty {l.quantity}
                    </p>
                    <PackBreakdown line={l} />
                  </div>
                </div>
                <p className="text-sm tabular-nums text-black">
                  {formatPrice(l.lineTotal)}
                </p>
              </li>
            ))}
          </ul>

          <dl className="mt-6 space-y-2 border-t border-black/10 pt-4 text-sm">
            <Row label="Subtotal" value={formatPrice(order.pricing.subtotal)} />
            {order.pricing.discount > 0 && order.pricing.promo && (
              <Row
                label={`Promo (${order.pricing.promo.code})`}
                value={`− ${formatPrice(order.pricing.discount)}`}
              />
            )}
            <Row
              label={`Shipping · ${order.delivery.label}`}
              value={
                order.pricing.shipping === 0 ? "Complimentary" : formatPrice(order.pricing.shipping)
              }
            />
            {order.pricing.gst && order.pricing.gst.mode !== "none" ? (
              <>
                <Row label="Taxable amount" value={formatPrice(order.pricing.gst.taxable)} />
                {order.pricing.gst.mode === "cgst_sgst" ? (
                  <>
                    <Row
                      label={`CGST @ ${order.pricing.gst.cgstRatePct ?? order.pricing.gst.ratePct / 2}%`}
                      value={formatPrice(order.pricing.gst.cgst)}
                    />
                    <Row
                      label={`SGST @ ${order.pricing.gst.sgstRatePct ?? order.pricing.gst.ratePct / 2}%`}
                      value={formatPrice(order.pricing.gst.sgst)}
                    />
                  </>
                ) : (
                  <Row
                    label={`IGST @ ${order.pricing.gst.igstRatePct ?? order.pricing.gst.ratePct}%`}
                    value={formatPrice(order.pricing.gst.igst)}
                  />
                )}
                <Row
                  label={`Total ${order.pricing.gst.label}${order.pricing.gst.inclusive ? " (incl.)" : ""}`}
                  value={formatPrice(order.pricing.gst.totalGst)}
                />
                {order.pricing.gst.placeOfSupply ? (
                  <Row label="Place of supply" value={order.pricing.gst.placeOfSupply} />
                ) : null}
              </>
            ) : null}
            <div className="mt-2 border-t border-black/10 pt-3">

              <Row label="Total paid" value={formatPrice(order.pricing.total)} strong />
            </div>
          </dl>

          <div className="mt-6 grid gap-6 border-t border-black/10 pt-6 sm:grid-cols-2">
            <AddressBlock title="Shipping Address" address={order.shipping} />
            <AddressBlock title="Billing Address" address={order.billing} />
          </div>

          <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-black/50">
            Payment · {paymentLabel}
            {order.razorpayPaymentId ? ` · Ref ${order.razorpayPaymentId}` : ""}
          </p>
        </div>

        <div className="mt-10 border border-dashed border-black/15 bg-black/[0.015] p-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-black/60">
            <Sparkles className="h-3.5 w-3.5 text-[#C6A15B]" /> Track Your Order
          </div>
          <p className="mt-2 text-sm text-black/70">
            Live tracking will appear here once your parcel is dispatched. We'll email a tracking
            link to <span className="font-medium text-black">{order.email}</span>.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/invoice/$id"
            params={{ id: order.id }}
            className="inline-flex items-center justify-center gap-2 border border-black/20 bg-white px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B]"
          >
            <Download className="h-3.5 w-3.5" /> Download Invoice
          </Link>
          <Link
            to="/fragrances"
            className="inline-flex items-center justify-center border border-black bg-black px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition hover:bg-[#C6A15B] hover:border-[#C6A15B]"
          >
            Continue Shopping
          </Link>
          <Link
            to="/"
            className="inline-flex items-center justify-center border border-black/20 bg-white px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B]"
          >
            Return Home
          </Link>
        </div>
      </div>
    </div>
  );
}

function Card({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-black/10 p-5">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-black/60">
        {icon}
        {title}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <dt className="text-[11px] uppercase tracking-[0.22em] text-black/60">{label}</dt>
      <dd
        className={"tabular-nums " + (strong ? "text-base font-medium text-black" : "text-black")}
      >
        {value}
      </dd>
    </div>
  );
}

function AddressBlock({
  title,
  address,
}: {
  title: string;
  address: import("@/lib/checkout/schema").Address;
}) {
  return (
    <div>
      <p className="mb-2 text-[11px] uppercase tracking-[0.22em] text-black/60">{title}</p>
      <address className="not-italic text-sm text-black/80">
        {address.fullName}
        <br />
        {address.line1}
        {address.line2 ? (
          <>
            <br />
            {address.line2}
          </>
        ) : null}
        <br />
        {address.city}, {address.postalCode}
        <br />
        {address.country}
        <br />
        <span className="text-black/50">{address.phone}</span>
      </address>
    </div>
  );
}

function SkeletonState() {
  return (
    <div className="mx-auto max-w-4xl px-4 py-24">
      <div className="mx-auto h-8 w-40 animate-pulse bg-black/5" />
      <div className="mx-auto mt-4 h-4 w-72 animate-pulse bg-black/5" />
      <div className="mt-10 h-64 animate-pulse bg-black/5" />
    </div>
  );
}

function NotFoundState({ id }: { id: string }) {
  return (
    <div className="mx-auto max-w-2xl px-4 py-24 text-center">
      <h1 className="text-2xl text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
        Order not found
      </h1>
      <p className="mt-3 text-sm text-black/60">
        We couldn't find order <span className="font-medium">{id}</span> on this device. Orders are
        stored locally until account sync is enabled.
      </p>
      <Link
        to="/"
        className="mt-6 inline-flex items-center justify-center border border-black bg-black px-6 py-3 text-[11px] font-medium uppercase tracking-[0.22em] text-white transition hover:bg-[#C6A15B] hover:border-[#C6A15B]"
      >
        Return Home
      </Link>
    </div>
  );
}
